import { getMimeExtension } from './ufile-utils.js'
import { getDirPath } from './ufile-utils.js'
import { nanoid } from '@w/id'
import { TRPCError } from '@trpc/server'

// this info is what we pass through network
export interface UFileInfo {
  readonly type: string
  readonly size: number
  readonly path: string
  readonly meta?: Record<string, unknown>
}

export class UFile implements UFileInfo {
  id = nanoid() // to distinguish files with the same name (serves as a in memory pointer)
  path = ''
  blob: Blob = new Blob([])
  meta: Record<string, unknown> | undefined

  static canAccept(
    params: { maxSize: number },
    ctx: { _getHeader: (key: string) => string | undefined },
  ) {
    const info = _getUFileInfoFromTrpcHeaders(ctx._getHeader)
    return info.size <= params.maxSize
  }

  static validate(
    params: { maxSize: number; allowedTypes?: string[] },
    ctx: { _getHeader: (key: string) => string | undefined },
  ): string {
    const info = _getUFileInfoFromTrpcHeaders(ctx._getHeader)

    if (info.size > params.maxSize) return 'file is too large'
    if (params.allowedTypes && !params.allowedTypes.includes(info.type))
      return 'file is not allowed'
    return ''
  }

  static assert(
    params: { maxSize: number; allowedTypes?: string[] },
    ctx: { _getHeader: (key: string) => string | undefined },
  ) {
    const error = UFile.validate(params, ctx)
    if (error) throw new TRPCError({ code: 'BAD_REQUEST', message: error })
  }

  static async fromStream(
    stream: ReadableStream,
    ctx: { _getHeader: (key: string) => string | undefined },
  ) {
    const blob = await new Response(stream).blob()
    const info = _getUFileInfoFromTrpcHeaders(ctx._getHeader)
    return new UFile(blob, { path: info.path, type: info.type, meta: info.meta })
  }

  // file is File, Blob, UFile, Uint8Array, or object (json)
  constructor(file?: a, opts?: { path?: string; type?: string; meta?: a }) {
    if (!file) this.blob = new Blob([])

    if (opts?.path) this.path = opts.path
    else {
      if (file instanceof File) this.path = file.name
      else if (file instanceof Blob) this.path = `${nanoid()}${getMimeExtension(file.type)}`
      else if (file instanceof UFile) this.path = file.path
      else if (isObject(file)) this.path = `${nanoid()}.json`
    }

    if (file instanceof File) {
      this.blob = new Blob([file], { type: file.type })
      // capture lastModified into meta and path from name
      this.meta = { ...(opts?.meta || {}), lastModified: file.lastModified }
      if (!opts?.path) this.path = file.name
    } else if (file instanceof Blob) this.blob = new Blob([file], { type: file.type })
    else if (file instanceof UFile) {
      this.blob = file.blob
      this.meta = file.meta
      this.path = file.path
    } else if (file instanceof Uint8Array)
      // ensure correct BlobPart typing across TS DOM lib versions
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      this.blob = new Blob([file as any], { type: 'application/octet-stream' })
    // should be last since ArrayBuffer is Object too
    else if (isObject(file)) this.blob = objectToBlob(file)
    // else // probably undefined

    if (opts?.type) this.blob = new Blob([this.blob], { type: opts.type })

    if (!this.meta && opts?.meta) this.meta = opts.meta
  }

  toTrpc(): Blob {
    const result = this.blob as Blob & { _ufilePath?: string; _ufileMeta?: string }

    result._ufilePath = this.path
    if (this.meta) result._ufileMeta = JSON.stringify(this.meta)
    return result
  }

  get dirPath() {
    return getDirPath(this.path)
  }

  set dirPath(dirPath: string) {
    this.path = `${dirPath}/${this.name}`
  }

  setDirPath(dirPath: string) {
    this.dirPath = dirPath
    return this
  }

  setPath(path: string) {
    this.path = path
    return this
  }

  moveTo(dirPath: string) {
    this.path = `${dirPath}/${this.path}`
    return this
  }

  makeNameUnique(id?: string) {
    this.name = `${this.fileNameWithoutExtension}-${id || nanoid()}.${this.extension}`
    return this
  }

  get name() {
    return this.path.split('/').pop() || ''
  }

  set name(name: string) {
    if (this.dirPath) this.path = `${this.dirPath}/${name}`
    else this.path = name
  }

  setName(name: string) {
    this.name = name
    return this
  }

  get extension() {
    return this.name.split('.').at(-1) || ''
  }

  set extension(ext: string) {
    this.name = `${this.fileNameWithoutExtension}.${ext}`
  }

  get fileNameWithoutExtension() {
    return this.name.split('.').slice(0, -1).join('.')
  }

  get size() {
    return this.blob.size
  }

  get type(): string {
    return this.blob.type
  }

  get isImage() {
    return this.type.startsWith('image')
  }

  get isVideo() {
    return this.type.startsWith('video')
  }

  get isAudio() {
    return this.type.startsWith('audio')
  }

  getMeta<T>(): T {
    return this.meta as T
  }

  createObjectURL(): string {
    const url = URL as a
    if (!url.createObjectURL) throw new Error('createObjectURL not supported') // on backend
    return url.createObjectURL(this.blob)
  }

  arrayBuffer(): Promise<ArrayBuffer> {
    return this.blob.arrayBuffer()
  }

  uint8Array(): Promise<Uint8Array> {
    return this.arrayBuffer().then((ab) => new Uint8Array(ab))
  }

  json<T>(): Promise<T> {
    return blobToObject(this.blob)
  }

  toString() {
    return `UFile(${this.name} ${this.size} ${this.id})`
  }

  clone() {
    return new UFile(this.blob, { path: this.path })
  }
}

export type SetUFile = (_: UFile) => void
export type UFiles = UFile[]
export type SetUFiles = (_: UFiles) => void

const objectToBlob = (obj: unknown) => new Blob([JSON.stringify(obj)], { type: 'application/json' })
const blobToObject = async (blob: Blob) => JSON.parse(await blob.text())

const isObject = (obj: unknown) => obj !== null && typeof obj === 'object'

// Header values must be ISO-8859-1: fetch throws on anything above U+00FF (€, „“, –, emoji),
// and the email subject/body travel in x-ufile-meta. Such values are percent-encoded and flagged
// with x-ufile-encoding; everything else is sent raw, so requests that work today are unchanged.
const isLatin1 = (value: string) => [...value].every((char) => (char.codePointAt(0) ?? 0) <= 0xff)

export const _getTrpcClientHeadersForUFileBlob = (blob_: Blob) => {
  const blob = blob_ as Blob & { _ufilePath?: string; _ufileMeta?: string }
  const result: Record<string, string> = {}
  const encode = [blob._ufilePath, blob._ufileMeta].some((value) => value && !isLatin1(value))
  const toHeader = (value: string) => (encode ? encodeURIComponent(value) : value)
  if (blob._ufilePath) result['x-ufile-path'] = toHeader(blob._ufilePath)
  if (blob._ufileMeta) result['x-ufile-meta'] = toHeader(blob._ufileMeta)
  if (encode) result['x-ufile-encoding'] = 'uri'
  result['x-content-size'] = blob.size.toString()
  result['x-content-type'] = blob.type
  return result
}

export const _getUFileInfoFromTrpcHeaders = (
  getHeader: (key: string) => string | undefined,
): UFileInfo => {
  // should consider standard headers and 2 custom headers
  // if required headers are missing, throw an error

  const encoded = getHeader('x-ufile-encoding') === 'uri'
  const fromHeader = (key: string) => {
    const value = getHeader(key)
    return value && encoded ? decodeURIComponent(value) : value
  }
  const meta = fromHeader('x-ufile-meta')

  const result: UFileInfo = {
    type: getHeader('x-content-type') || getHeader('content-type') || '',
    path: fromHeader('x-ufile-path') || '',
    size: Number.parseInt(getHeader('x-content-size') || getHeader('content-length') || '0'),
    meta: meta ? JSON.parse(meta) : undefined,
  }

  if (!result.type) throw new Error('x-content-type or content-type header is required')
  if (!result.size)
    throw new Error(
      `x-content-size or content-length header is required: ${getHeader('x-content-size') || getHeader('content-length')}`,
    )
  if (!result.path) throw new Error('x-ufile-path header is required')

  return result
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type a = any
